import { Fade, Stack, Typography, useTheme } from "@mui/material";
import { Issue } from "generated-graphql/graphql";
import { groupBy } from "lodash-es";
import { PropsWithChildren } from "react";
import { ControlledBigTooltip } from "./ControlledBigTooltip";

type IssueListTooltipProps = PropsWithChildren<{
  issues: Issue[];
  open: boolean;
  onClose: () => void;
  getIssueDescription?: (issue: Issue) => string;
  header?: string;
}>;

export const IssueListTooltip = ({
  issues,
  open,
  onClose,
  children,
  getIssueDescription,
  header,
}: IssueListTooltipProps) => {
  const theme = useTheme();
  const issuesByModel = groupBy(issues, (i) => i.modelName);

  return (
    <ControlledBigTooltip
      open={open}
      onClose={onClose}
      placement="top-end"
      slots={{
        transition: Fade,
      }}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: theme.spacing(49),
          },
        },
      }}
      title={
        <Stack
          gap={1}
          sx={{
            maxHeight: theme.spacing(75),
            overflowY: "auto",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" fontSize={"12px"}>
            {header}
          </Typography>
          <Typography
            component="ul"
            sx={{
              pl: 2,
              fontSize: "12px",
              "& li": { mb: 0.5 },
            }}
          >
            {Object.keys(issuesByModel).map((key) => {
              const modelIssues = issuesByModel[key];
              return (
                <>
                  {modelIssues.map((issue, j) => {
                    const description = getIssueDescription
                      ? getIssueDescription(issue)
                      : issue.message;
                    return <li key={j}>{description}</li>;
                  })}
                </>
              );
            })}
          </Typography>
        </Stack>
      }
    >
      <div>{children}</div>
    </ControlledBigTooltip>
  );
};
